import React, { useEffect, useState, createContext, useContext } from 'react';
export type UserRole = 'doctor' | 'nurse' | 'lab_scientist' | 'hygienist' | 'ipc_officer' | 'admin';
export type Ward = 'surgical' | 'medical' | 'ae' | 'tb' | 'isolation' | 'labour' | 'laboratory';
interface User {
  id: string;
  name: string;
  shortname: string;
  role: UserRole;
  email: string;
  phone?: string;
  employeeId?: string;
}
interface StaffMember {
  id: string;
  name: string;
  role: string;
  ward: Ward;
}
interface AppContextType {
  user: User | null;
  currentWard: Ward;
  setCurrentWard: (ward: Ward) => void;
  isOnline: boolean;
  lastSynced: Date | null;
  isAuthenticated: boolean;
  logout: () => void;
  allStaff: StaffMember[];
}
const AppContext = createContext<AppContextType | undefined>(undefined);
export function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [user] = useState<User>({
    id: 'user-001',
    name: 'Dr. Sarah Johnson',
    shortname: 'Dr. Sarah',
    role: 'doctor',
    email: 'sarah.johnson@hospital.com',
    phone: '+234 803 456 7890',
    employeeId: 'EMP-2024-001'
  });
  const [currentWard, setCurrentWard] = useState<Ward>('surgical');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSynced, setLastSynced] = useState<Date | null>(new Date());
  // Mock staff data from all wards
  const [allStaff] = useState<StaffMember[]>([{
    id: 's1',
    name: 'Dr. Sarah Johnson',
    role: 'Doctor',
    ward: 'surgical'
  }, {
    id: 's2',
    name: 'Nurse Mary Williams',
    role: 'Nurse',
    ward: 'surgical'
  }, {
    id: 's3',
    name: 'Dr. John Smith',
    role: 'Doctor',
    ward: 'medical'
  }, {
    id: 's4',
    name: 'Nurse Peter Brown',
    role: 'Nurse',
    ward: 'medical'
  }, {
    id: 's5',
    name: 'Dr. Emily Davis',
    role: 'Doctor',
    ward: 'ae'
  }, {
    id: 's6',
    name: 'Nurse James Wilson',
    role: 'Nurse',
    ward: 'ae'
  }, {
    id: 's7',
    name: 'Dr. Michael Chen',
    role: 'Doctor',
    ward: 'tb'
  }, {
    id: 's8',
    name: 'Nurse Lisa Anderson',
    role: 'Nurse',
    ward: 'tb'
  }, {
    id: 's9',
    name: 'Dr. Rachel Green',
    role: 'Doctor',
    ward: 'isolation'
  }, {
    id: 's10',
    name: 'Nurse David Martinez',
    role: 'Nurse',
    ward: 'isolation'
  }, {
    id: 's11',
    name: 'Dr. Jennifer Lee',
    role: 'Doctor',
    ward: 'labour'
  }, {
    id: 's12',
    name: 'Midwife Sarah Thompson',
    role: 'Midwife',
    ward: 'labour'
  }, {
    id: 's13',
    name: 'Lab Scientist Tom Harris',
    role: 'Lab Scientist',
    ward: 'laboratory'
  }, {
    id: 's14',
    name: 'Lab Tech Anna White',
    role: 'Lab Technician',
    ward: 'laboratory'
  }]);
  const logout = () => {
    setIsAuthenticated(false);
  };
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  return <AppContext.Provider value={{
    user,
    currentWard,
    setCurrentWard,
    isOnline,
    lastSynced,
    isAuthenticated,
    logout,
    allStaff
  }} data-id="element-68">
      {children}
    </AppContext.Provider>;
}
export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}